import React, { ReactElement } from 'react';
import { PriceRange } from '../../apps/benefit/models/PriceRange';

interface IRangeSliderProps {
  min: number;
  max: number;
  value: PriceRange;
  step?: number;
  label?: ReactElement;
  onChangeValue: (range: PriceRange) => void;
  activeSliding?: (value: boolean) => void;
}

const RangeSlider = (props: IRangeSliderProps) => {
  const { min, max, value, step, label, onChangeValue, activeSliding } = props;

  const [minValue, setMinValue] = React.useState(value.min);
  const [maxValue, setMaxValue] = React.useState(value.max);

  const minPos = ((minValue - min) / (max - min)) * 100;
  const maxPos = ((maxValue - min) / (max - min)) * 100;

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newMinVal = Math.min(+event.target.value, maxValue - (step ?? 1));
    if (!value) setMinValue(newMinVal);
    onChangeValue({ min: newMinVal, max: maxValue });
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newMaxVal = Math.max(+event.target.value, minValue + (step ?? 1));
    if (!value) setMaxValue(newMaxVal);
    onChangeValue({ min: minValue, max: newMaxVal });
  };

  const handleRailClick = (event: any) => {
    event.preventDefault();
    const railRect = event.currentTarget.getBoundingClientRect();
    const railWidth = railRect.width;
    const clickPosition = event.clientX - railRect.left;
    const rangeClickPercent = Math.round((clickPosition * 100) / railWidth);
    const newValue = Math.round((rangeClickPercent / 100) * (max - min) + min);
    if (newValue - minValue < maxValue - newValue) {
      setMinValue(newValue);
      onChangeValue({ min: newValue, max: maxValue });
    } else {
      setMaxValue(newValue);
      onChangeValue({ min: minValue, max: newValue });
    }
  };

  const handleMouseDown = () =>
    activeSliding ? activeSliding(true) : undefined;

  const handleMouseUp = () =>
    activeSliding ? activeSliding(false) : undefined;

  React.useEffect(() => {
    if (value) {
      setMinValue(value.min);
      setMaxValue(value.max);
    }
  }, [value]);

  return (
    <div className="range-slider">
      {!!label && label}
      <div className="slider-outer">
        <div className="slider-wrapper">
          <div className="input-wrapper">
            <input
              className="range-input"
              type="range"
              value={minValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMinChange}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
            />
            <input
              className="range-input"
              type="range"
              value={maxValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMaxChange}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
            />
          </div>

          <div className="control-wrapper">
            <div className="control left" style={{ left: `${minPos}%` }} />
            <div className="rail" onClick={handleRailClick}>
              <div
                className="inner-rail"
                style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }}
              />
            </div>
            <div className="control right" style={{ left: `${maxPos}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
